import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf, faDownload } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button';

/**
 * Reusable Export Button Component
 * Shows loading state with spinner when exporting
 * Supports single export (onClick) or dropdown menu (onExportExcel + onExportPdf)
 *
 * @param {Boolean} loading - Is export in progress
 * @param {Boolean} disabled - Additional disabled condition
 * @param {Function} onClick - Click handler for single export type
 * @param {Function} onExportExcel - Click handler for Excel export (dropdown mode)
 * @param {Function} onExportPdf - Click handler for PDF export (dropdown mode)
 * @param {String} variant - Button variant (default: "success" for excel, "error" for pdf)
 * @param {String} size - Button size (default: "lg")
 * @param {String} type - Export type: "excel" or "pdf" (default: "excel") - used in single mode
 * @param {String} label - Button label translation key (overrides type default)
 * @param {String} loadingLabel - Loading label translation key (default: "common.exporting")
 * @param {String} id - Button ID
 * @param {String} name - Button name
 * @param {String} data-testid - Test ID
 */
function ExportButton({
  loading = false,
  disabled = false,
  onClick,
  onExportExcel,
  onExportPdf,
  variant,
  size = 'lg',
  type = 'excel',
  label,
  loadingLabel = 'common.exporting',
  id,
  name,
  'data-testid': dataTestId,
  ...props
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Determine if we're in dropdown mode (both Excel and PDF handlers provided)
  const isDropdownMode = onExportExcel && onExportPdf;

  // Determine defaults based on type (for single mode)
  const isPdf = type === 'pdf';
  const defaultLabel = isDropdownMode ? 'common.export' : (isPdf ? 'common.exportPdf' : 'common.exportExcel');
  const defaultIcon = isDropdownMode ? 'fas fa-download' : (isPdf ? 'fas fa-file-pdf' : 'fas fa-file-excel');
  const defaultVariant = isDropdownMode ? 'outline' : (isPdf ? 'danger' : 'success');
  const defaultId = isDropdownMode ? 'export' : (isPdf ? 'exportPdf' : 'exportExcel');

  const finalLabel = label || defaultLabel;
  const finalVariant = variant || defaultVariant;
  const finalId = id || defaultId;
  const finalName = name || finalId;

  const handleClick = (event) => {
    if (isDropdownMode) {
      setAnchorEl(event.currentTarget);
    } else if (onClick) {
      onClick(event);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportExcel = () => {
    handleClose();
    if (onExportExcel) {
      onExportExcel();
    }
  };

  const handleExportPdf = () => {
    handleClose();
    if (onExportPdf) {
      onExportPdf();
    }
  };

  return (
    <>
      <Button
        variant={finalVariant}
        icon={loading ? null : defaultIcon}
        iconPosition="left"
        onClick={handleClick}
        disabled={loading || disabled}
        id={finalId}
        name={finalName}
        data-testid={dataTestId}
        size={size}
        {...props}
      >
        {loading ? (
          <>
            <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
            {t(loadingLabel)}
          </>
        ) : (
          t(finalLabel)
        )}
      </Button>
      {isDropdownMode && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={handleExportExcel}
            id={`${finalId}-excel`}
            data-testid={`${dataTestId}-excel`}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faFileExcel} style={{ color: '#217346' }} />
            </ListItemIcon>
            <ListItemText>{t('common.exportExcel')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleExportPdf}
            id={`${finalId}-pdf`}
            data-testid={`${dataTestId}-pdf`}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faFilePdf} style={{ color: '#dc3545' }} />
            </ListItemIcon>
            <ListItemText>{t('common.exportPdf')}</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
}

ExportButton.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onExportExcel: PropTypes.func,
  onExportPdf: PropTypes.func,
  variant: PropTypes.string,
  size: PropTypes.string,
  type: PropTypes.oneOf(['excel', 'pdf']),
  label: PropTypes.string,
  loadingLabel: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  'data-testid': PropTypes.string,
};

export default ExportButton;
